import { LaserUsageSchema, type MySections, type SectionSettingsInput, SectionSettingsSchema } from '@clinic/contracts';
import { createQuery, useQueryClient } from '@tanstack/svelte-query';
import { createMutation } from '../api/mutation';
import { apiRequest } from '../api/client';
import { keys } from '../api/keys';

export const NO_SECTIONS: MySections = { desk: [], cash: [], clinical: [] };

/**
 * Who handles the laser desk and till, and the sections the caller reaches (ADR-036). The org
 * route reads it once for a clinic; the pages read the answer from `useOrg().sections`.
 */
export function createSectionSettings(orgId: () => string, enabled: () => boolean = () => true) {
  return createQuery(() => ({
    queryKey: keys.sectionSettings(orgId()),
    queryFn: ({ signal }) =>
      apiRequest('/v1/clinic/section-settings', { orgId: orgId(), schema: SectionSettingsSchema, signal }),
    enabled: enabled(),
  }));
}

/** What turning laser off hides (ADR-043), read when the admin is about to confirm. */
export function createLaserUsage(orgId: () => string, enabled: () => boolean) {
  return createQuery(() => ({
    queryKey: [...keys.sectionSettings(orgId()), 'laser-usage'] as const,
    queryFn: ({ signal }) =>
      apiRequest('/v1/clinic/section-settings/laser-usage', { orgId: orgId(), schema: LaserUsageSchema, signal }),
    enabled: enabled(),
    staleTime: 0,
  }));
}

/** The admin's choice; every query of the clinic is stale afterwards (what each desk sees changed). */
export function createSaveSectionSettings(orgId: () => string) {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (body: SectionSettingsInput) =>
      apiRequest('/v1/clinic/section-settings', { method: 'PUT', orgId: orgId(), body, schema: SectionSettingsSchema }),
    onSuccess: (data) => {
      qc.setQueryData(keys.sectionSettings(orgId()), data);
      void qc.invalidateQueries({ queryKey: keys.org(orgId()) });
    },
  }));
}
